import React, { useEffect } from 'react';
import { filter, find, flatMap, get, includes, some } from 'lodash';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useTranslate } from '../../../../../core/translate';
import MoreViewsTwoD from '../../../../main/moreViews/MoreViewsTwoD';
import GridView from '../../../../views/gridView/GridView';
import TrimFeaturesModal from '../features/trimFeaturesModal/TrimFeaturesModal';
import TrimCard from '../TrimCard';
import TrimCompareModal from '../trimCompareModal/TrimCompareModal';
import { TRIMS } from '../../../../common/steps';
import { findClosestMsrp } from '../../findClosestMsrp';
import { toUniqueTrims, normalizeDriveAndWheelTypes } from '../utils';
import DriveTypeFilter from '../filters/DriveTypeFilter';
import useAnimations from '../hooks/useAnimations';
import AlignCardsContent from '../../../../common/AlignCardsContent';
import {
  dispatchCustomEvent,
  BYO_DRIVE_TYPE_CHANGE,
} from '../../../../utils/customMessage';

const TrimWithoutFiltering = (props) => {
  const {
    config,
    disclaimer,
    appResources,
    breakpoint,
    disableCompare,
    isSelectionRevealed,
    onChange,
    isUpdating,
    features,
    updatingOption,
    postalCode,
    modelInfo,
    bottomOffset,
    setBottomOffset,
    animation,
    trims,
    trimMatrix,
  } = props;

  const { selections, msrp } = config;
  const { bodyTypes } = trimMatrix;
  const totalMsrpWithOptions = get(msrp, 'totalMsrpWithOptions');
  const translate = useTranslate();
  const navigate = useNavigate();
  const { setToggled } = useAnimations(animation);

  useEffect(() => {
    dispatchCustomEvent(BYO_DRIVE_TYPE_CHANGE);
  }, [selections]);

  const onClose = () => {
    navigate('');
  };

  const trimsWithSelectedBodyType = filter(
    trims,
    (x) => x.bodyTypeId === config.bodyType.id
  );

  const hasCabSizes = some(bodyTypes, 'cabSize');

  const onDriveChange = ({ driveType, rearWheelType }) => {
    const { trims, config, onChange } = props;
    const { trimName, engine, transmission, cabSize } = config.selections;

    const filteredTrims = filter(trims, (trim) =>
      hasCabSizes
        ? trim.cabSize === cabSize
        : trim.bodyTypeId === selections.bodyTypeId
    );

    const options = flatMap(filteredTrims, 'options');
    const msrp = get(config, 'msrp.totalMsrpWithOptions');
    const filteredOptions = filter(options, { driveType, rearWheelType });

    const option =
      find(filteredOptions, {
        trimName,
        engineCode: engine,
        transmissionCode: transmission,
      }) ||
      findClosestMsrp(filter(filteredOptions, { trimName })) ||
      findClosestMsrp(filteredOptions, msrp);

    onChange(option, true);
    setToggled(true);
  };

  const filteredBodyType =
    bodyTypes &&
    bodyTypes.filter((bodyType) =>
      hasCabSizes
        ? bodyType.cabSize === selections.cabSize
        : bodyType.id === selections.bodyTypeId
    );

  const driveAndWheelTypes = flatMap(filteredBodyType, 'driveTypes');

  const uniqueDriveAndWheelTypes = normalizeDriveAndWheelTypes(
    driveAndWheelTypes
  );

  const trimsMatchDriveType = trimsWithSelectedBodyType.filter((trim) => {
    const driveType = get(trim, 'driveType.id');
    const rearWheelType = get(trim, 'rearWheelType');

    return (
      driveType === selections.driveType &&
      rearWheelType === selections.rearWheelType
    );
  });
  const trimsCount = trimsWithSelectedBodyType.length;

  const trimsNotMatchDriveType = trimsWithSelectedBodyType.filter((trim) => {
    const driveType = get(trim, 'driveType.id');
    const rearWheelType = get(trim, 'rearWheelType');
    return (
      driveType !== selections.driveType ||
      rearWheelType !== selections.rearWheelType
    );
  });

  const trimNamesInSelectedDriveType = toUniqueTrims(trimsMatchDriveType);
  const trimsOnlyInUnselectedDriveType = trimsNotMatchDriveType.filter(
    (trim) => !includes(trimNamesInSelectedDriveType, trim.trimName)
  );

  const filteredTrims = [
    ...trimsOnlyInUnselectedDriveType,
    ...trimsMatchDriveType,
  ];

  const displayTrims = filter(trimsWithSelectedBodyType, (trim) => {
    const filterTrim = find(filteredTrims, { styleId: trim.styleId });
    if (filterTrim) return filterTrim;
  });

  const trimGroups = displayTrims.map((trim) => {
    return {
      ...trim,
      displayOnlyLabel: trim.driveType.id !== selections.driveType,
    };
  });

  const trimsHeader = translate(
    trimGroups.length === 1
      ? 'component.common.select.single.trim'
      : 'component.common.select.trim'
  );

  return (
    <>
      <Routes>
        <Route
          path="/features/:styleId/*"
          element={
            <TrimFeaturesModal
              appResources={appResources}
              setBottomOffset={setBottomOffset}
              features={features}
              breakpoint={breakpoint}
              disclaimer={disclaimer}
              selections={selections}
              disableCompare={disableCompare}
              trimGroups={trimGroups}
              isSelectionRevealed={isSelectionRevealed}
              onClose={() => {
                onClose();
              }}
              onSelect={(option) => {
                onChange(option);
              }}
            />
          }
        />
        <Route
          path={`/compare/*`}
          element={
            <TrimCompareModal
              onClose={onClose}
              onSelect={onChange}
              config={config}
              modelInfo={modelInfo}
              postalCode={postalCode}
              selections={selections}
              trimGroups={trimGroups}
              isSelectionRevealed={isSelectionRevealed}
              hasCabSizes={hasCabSizes}
            />
          }
        />
        {/* TODO: Remove the corvette case once the deployment to production happens and enableVisualizer has correct vals */}
        <Route
          path="/features/:styleId/moreviews"
          element={<MoreViewsTwoD />}
        />
      </Routes>
      <GridView
        step={TRIMS}
        bottomOffset={isSelectionRevealed ? bottomOffset : '0px'}
        driveTypeFilter={
          <DriveTypeFilter
            isUpdating={isUpdating}
            uniqueDriveAndWheelTypes={uniqueDriveAndWheelTypes}
            selections={selections}
            onDriveChange={onDriveChange}
            isSelectionRevealed={isSelectionRevealed}
            onClose={onClose}
          />
        }
        isSelectionRevealed={isSelectionRevealed}
        options={
          <AlignCardsContent numberOfCards={trimsCount}>
            {({ maxHeight, getRefByIndex }) => (
              <>
                {trimGroups.map((trim, index) => (
                  <TrimCard
                    trim={trim}
                    key={trim.styleId}
                    selections={selections}
                    disclaimer={disclaimer}
                    analyticsKey={trimsHeader}
                    msrp={totalMsrpWithOptions}
                    onChange={onChange}
                    isSelectionRevealed={isSelectionRevealed}
                    cardIndex={index}
                    displayOnlyLabel={trim.displayOnlyLabel}
                    isUpdating={isUpdating}
                    isUpdatingOption={
                      isUpdating &&
                      get(updatingOption, 'styleId') === trim.styleId
                    }
                    wait={index * 200}
                    animation={animation}
                    primaryRef={getRefByIndex(index)}
                    primaryDivHeight={maxHeight}
                  />
                ))}
              </>
            )}
          </AlignCardsContent>
        }></GridView>
    </>
  );
};

export default TrimWithoutFiltering;
